import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, MailCheck } from "lucide-react";

export default function Signup() {
  const { signup, user } = useAuth();
  const nav = useNavigate();
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [verifySentEmail, setVerifySentEmail] = useState("");
  const [resending, setResending] = useState(false);

  useEffect(() => { if (user) nav("/dashboard"); }, [user, nav]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error(t("auth.signup_error_password_short")); return; }
    setBusy(true);
    try {
      const data = await signup(email, password, name);
      if (data?.verification_required) {
        setVerifySentEmail(email);
      } else {
        // Legacy path (backend returned a token immediately).
        toast.success(t("auth.signup_success_legacy"));
        nav("/dashboard");
      }
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      const code = err?.code;
      let msg;
      if (status === 400 && typeof detail === "string" && /exist|already/i.test(detail)) {
        msg = t("auth.signup_error_account_exists");
      } else if (status === 409) {
        msg = t("auth.signup_error_account_exists");
      } else if (typeof detail === "string" && detail.length > 0) {
        msg = detail;
      } else if (code === "ECONNABORTED" || /timeout/i.test(err?.message || "")) {
        msg = t("auth.signup_error_timeout");
      } else if (!err?.response) {
        msg = t("auth.signup_error_unavailable");
      } else if (status >= 500) {
        msg = t("auth.signup_error_server");
      } else {
        msg = t("auth.signup_error_generic");
      }
      toast.error(msg);
    }
    setBusy(false);
  };

  const google = () => {
    const redirect = `${window.location.origin}/login`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
  };

  const resend = async () => {
    if (!verifySentEmail || resending) return;
    setResending(true);
    try {
      await api.post("/auth/resend-verification", { email: verifySentEmail });
      toast.success(t("auth.signup_resend_toast_ok"));
    } catch (_) {
      toast.error(t("auth.signup_resend_toast_err"));
    }
    setResending(false);
  };

  if (verifySentEmail) {
    return (
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md text-center" data-testid="signup-verify-sent">
          <MailCheck className="w-14 h-14 mx-auto text-blue-400 mb-6" />
          <h1 className="text-3xl font-semibold tracking-tight mb-2">{t("auth.signup_check_email_title")}</h1>
          <p className="text-sm text-zinc-400 mb-2">
            {t("auth.signup_verification_sent_to")}
            <span className="text-white font-mono ml-1" data-testid="signup-verify-email">{verifySentEmail}</span>.
          </p>
          <p className="text-sm text-zinc-500 mb-8">
            {t("auth.signup_expires_hint")}
          </p>
          <div className="flex gap-3 justify-center flex-wrap">
            <Button
              onClick={resend}
              disabled={resending}
              data-testid="signup-resend-btn"
              className="bg-blue-600 hover:bg-blue-500 text-white"
            >
              {resending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t("auth.signup_resending")}</> : t("auth.signup_resend")}
            </Button>
            <Link to="/login" data-testid="signup-back-login">
              <Button variant="outline" className="border-[#1E212B] bg-[#12141C] hover:bg-[#161821] text-white">
                {t("auth.signup_back_to_login")}
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <h1 className="text-3xl font-semibold tracking-tight mb-2">{t("auth.signup_title")}</h1>
          <p className="text-sm text-zinc-500">{t("auth.signup_start_note")}</p>
        </div>
        <div className="bg-[#12141C] border border-[#1E212B] rounded-xl p-8">
          <Button onClick={google} data-testid="google-signup-btn" variant="outline"
                  className="w-full mb-4 bg-[#0F1119] border-[#1E212B] hover:bg-[#161821] hover:border-[#2D313F] text-white h-11">
            <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
            {t("auth.signup_google")}
          </Button>
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-[#1E212B]" /></div>
            <span className="relative flex justify-center text-xs text-zinc-500 bg-[#12141C] px-2 font-mono">{t("auth.login_or")}</span>
          </div>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="name" className="text-zinc-400 text-xs font-mono uppercase tracking-wider">{t("auth.name_label")}</Label>
              <Input id="name" data-testid="signup-name" required value={name} onChange={(e)=>setName(e.target.value)}
                     className="mt-1.5 bg-[#090A0F] border-[#1E212B] focus:border-blue-500 focus-visible:ring-blue-500/30 h-11" />
            </div>
            <div>
              <Label htmlFor="email" className="text-zinc-400 text-xs font-mono uppercase tracking-wider">{t("auth.email_label")}</Label>
              <Input id="email" data-testid="signup-email" type="email" required value={email} onChange={(e)=>setEmail(e.target.value)}
                     className="mt-1.5 bg-[#090A0F] border-[#1E212B] focus:border-blue-500 focus-visible:ring-blue-500/30 h-11" />
            </div>
            <div>
              <Label htmlFor="password" className="text-zinc-400 text-xs font-mono uppercase tracking-wider">{t("auth.password_label")}</Label>
              <Input id="password" data-testid="signup-password" type="password" required minLength={6} value={password} onChange={(e)=>setPassword(e.target.value)}
                     className="mt-1.5 bg-[#090A0F] border-[#1E212B] focus:border-blue-500 focus-visible:ring-blue-500/30 h-11" />
            </div>
            <Button type="submit" disabled={busy} data-testid="signup-submit" className="w-full bg-blue-600 hover:bg-blue-500 text-white h-11">
              {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />…</> : t("auth.signup_submit")}
            </Button>
          </form>
        </div>
        <p className="text-sm text-zinc-500 text-center mt-6">
          {t("auth.signup_have_account")} <Link to="/login" className="text-blue-400 hover:text-blue-300" data-testid="link-login">{t("auth.signup_login_link")}</Link>
        </p>
      </div>
    </div>
  );
}
